/**
 * Style adoption planning.
 *
 * *Adoption* is the one-time conversion of an object's literal material slots
 * into role bindings. Once adopted, applying or swapping a style is a single
 * commit of role values on an ancestor — bound slots re-resolve through the
 * provider walk — so this runs once per object, not per style change.
 *
 * The planner is **pure**: given an object's material slots and its authored
 * Renderer materials, it returns exactly what would be bound, what would be
 * left alone, and why. No scene access, no commits. That makes the whole
 * strategy testable against fixtures and lets the editor preview an adoption
 * before committing to it.
 *
 * Two facts about the engine shape this:
 *
 *  1. `targetMaterialIndex` is a MESH slot index. `detectMaterialSlots` emits
 *     one slot per mesh, so meshes that shared an authored material appear as
 *     several slots with the same `slotName`. We group by that name to decide,
 *     then fan the decision out to every slot in the group — a single material
 *     choice cannot be written once.
 *  2. A bound slot's authored field holds a `$ref` string rather than a value,
 *     which is what makes ownership structural: bound = style-owned, absent =
 *     never touched. An unbound literal is user-owned only **relative to a
 *     theme the object already follows** — on an object with no binding
 *     anywhere, a literal is simply the scene's pre-theme state, and the first
 *     adoption claims it (see `everAdopted` in {@link planAdoption}). This
 *     inference is sound only while partial adoption cannot be produced by the
 *     machinery itself — which is why `adoptObject` is atomic.
 */

import type { MaterialSlot } from './MaterialPreviews';
import type { MaterialData } from '../scenedata/behaviors/rendererdata';
import { isPropertyBinding, boundPropertyName } from '../behaviors/tracebehavior';
import {
    MATERIAL_ROLES,
    resolveMaterialRole,
    roleForPropertyName,
    type MaterialRole,
} from '../scenedata/material-roles';
import { isThemeProperty, resolveSurfaceColorRole } from '../scenedata/theme-vocabulary';
import type { SurfaceRole } from '../scenedata/theme-vocabulary';

/** One authored GLB material, and every mesh slot carrying it. */
export interface MaterialGroup {
    /** The GLB material name — the group key. */
    materialName: string;
    /** Every mesh slot index carrying this material, in slot order. */
    slotIndices: number[];
    /** The role this material's *name* declares, or null if it declares none. */
    declaredRole: MaterialRole | null;
    /**
     * The SURFACE role this material's name declares, or null. A different
     * axis from {@link declaredRole}, not a fallback for it: `fill_BG` names a
     * thing content sits on, and no material role describes that.
     */
    declaredSurfaceRole: SurfaceRole | null;
    /** Authored as flat content (KHR_materials_unlit) — see MaterialSlot.unlit. */
    unlit: boolean;
}

/**
 * Which rule the MATERIAL axis uses to decide roles — a property of the naming,
 * not a record of what happened.
 *
 * Two things it deliberately does not tell you:
 *
 * - **Whether anything was assigned.** `'ordinal'` is the answer whenever no
 *   name carried a material role, including when there was nothing to name:
 *   an object with no slots, one whose groups are all already bound, and one
 *   whose only adoptable groups are surface-named all report `'ordinal'` with
 *   an empty `assignments`. Read `assignments.length` for that question.
 * - **Anything about the surface axis**, which has no tier: a surface role is
 *   always declared by name ({@link SURFACE_COLOR_ALIASES}) and never falls
 *   back to position, so there is no second rule to report.
 */
export type AdoptionTier =
    /** At least one material name declared a role outright. */
    | 'role-name'
    /** No material name declared a role, so position is what decides. */
    | 'ordinal';

/** Why a group was left alone. */
export type AdoptionSkipReason =
    /** Already bound to a property — adoption is idempotent. */
    | 'already-bound'
    /**
     * Carries a value the user chose, which rebinding would silently discard: a literal
     * on an object a style has already adopted, or the object's own binding (a property
     * outside the theme vocabulary, such as one a state edit made) whose values a style
     * would leave shown in no state. Either way it is the user's work, not a style's.
     */
    | 'user-literal'
    /**
     * Authored flat content (KHR_materials_unlit) — a baked text label, image
     * plane, or UI panel. Its look IS its texture; re-materialing it destroys
     * the content, so themes leave it alone.
     */
    | 'unlit-content'
    /**
     * Sibling groups declare named roles but this group does not. Assigning it
     * an ordinal would scramble the named siblings, so it is left alone — no
     * consumer acts on this today, so the group keeps its authored material.
     */
    | 'no-declared-role';

export interface AdoptionAssignment {
    materialName: string;
    role: MaterialRole;
    /** Every mesh slot to bind. One material choice, N writes. */
    slotIndices: number[];
}

/**
 * A group bound to the SURFACE axis: colour only, because a surface role has
 * no src/roughness/metallic family to bind. Kept in its own list rather than
 * widened into {@link AdoptionAssignment} so a consumer that only knows about
 * material roles keeps compiling and simply does less.
 */
export interface SurfaceColorAssignment {
    materialName: string;
    role: SurfaceRole;
    /** Every mesh slot to bind. One colour choice, N writes. */
    slotIndices: number[];
}

export interface AdoptionSkip {
    materialName: string;
    slotIndices: number[];
    reason: AdoptionSkipReason;
}

/**
 * A bound group the plan wants UNBOUND — content that a pre-guard adoption
 * claimed. Releasing strips the adoption-written fields so the authored
 * material (the baked text/image texture) drives again; the guard then keeps
 * the group from being re-adopted. This is the repair path for assets damaged
 * before the unlit guard existed — without it the guard only protects assets
 * that were never styled.
 */
export interface AdoptionRelease {
    materialName: string;
    slotIndices: number[];
}

export interface AdoptionPlan {
    /**
     * Which strategy decided roles on this object. `'role-name'` does not mean
     * every group got one — unnamed siblings are skipped `'no-declared-role'`.
     */
    tier: AdoptionTier;
    assignments: AdoptionAssignment[];
    /**
     * Groups whose names declare a surface colour (`fill_BG`, `frame_BG`).
     * Empty for every asset that names none, which is most of them.
     */
    surfaceAssignments: SurfaceColorAssignment[];
    skipped: AdoptionSkip[];
    /** Bound content groups to unbind — see {@link AdoptionRelease}. */
    releases: AdoptionRelease[];
    /**
     * Mesh slots this plan touches, both axes. Not the number of property
     * writes: the material axis writes one per ROLE_SUFFIX per slot.
     */
    bindCount: number;
    /**
     * Human-readable oddities the plan proceeded through — e.g. two material
     * names declaring the same role. Never fatal (the plan stays executable);
     * editors surface them so a co-assignment is a choice, not a surprise.
     */
    warnings: string[];
}

export interface AdoptionOptions {
    /**
     * Leave slots carrying a hand-picked literal alone.
     * @default true
     */
    preserveUserLiterals?: boolean;
    /**
     * Adopt unlit (flat-content) materials too. The escape hatch for assets
     * that use unlit deliberately as a *surface* treatment rather than as
     * baked content.
     * @default false
     */
    adoptUnlit?: boolean;
}

/**
 * Collapse per-mesh slots into per-material groups.
 *
 * Grouping is by the authored material name, which is stable across re-export
 * in a way mesh order is not. Slot indices are collected in slot order so the
 * fan-out is deterministic.
 */
export function groupSlotsByMaterial(slots: MaterialSlot[]): MaterialGroup[] {
    const byName = new Map<string, MaterialGroup>();

    slots.forEach((slot, arrayIndex) => {
        const materialName = slot.slotName || `slot-${arrayIndex}`;
        // Mirrors the editor's slot addressing: materialIndex is authoritative,
        // array position is the fallback.
        const slotIndex = typeof slot.materialIndex === 'number' && Number.isFinite(slot.materialIndex)
            ? slot.materialIndex
            : arrayIndex;

        let group = byName.get(materialName);
        if (!group) {
            group = {
                materialName,
                slotIndices: [],
                declaredRole: resolveMaterialRole(materialName),
                declaredSurfaceRole: resolveSurfaceColorRole(materialName),
                unlit: false,
            };
            byName.set(materialName, group);
        }
        group.slotIndices.push(slotIndex);
        // One material, one flag: any slot marked unlit marks the group — the
        // slots share the authored material, so they cannot disagree.
        if (slot.unlit) group.unlit = true;
    });

    return [...byName.values()];
}

/** The authored entry for a slot, if the Renderer has one. */
function authoredFor(materials: MaterialData[] | undefined, slotIndex: number): MaterialData | undefined {
    return materials?.find(m => (m.targetMaterialIndex ?? 0) === slotIndex);
}

/**
 * Every slot field adoption writes. The factor fields are typed `number` but
 * carry the binding *string* when bound (the `opacity` precedent), so they are
 * inspected exactly like the string fields. A check that skips them treats a
 * factor-only edit or binding as "untouched" — which is how a hand-tuned
 * roughness would get silently rebound away.
 */
function adoptionFieldValues(entry: MaterialData): unknown[] {
    return [entry.src, entry.baseColorFactor, entry.roughnessFactor, entry.metallicFactor];
}

/** The property names the slots of a group are bound to, in the fields adoption would write. */
function boundNames(materials: MaterialData[] | undefined, group: MaterialGroup): string[] {
    return group.slotIndices.flatMap(i => {
        const entry = authoredFor(materials, i);
        return entry ? adoptionFieldValues(entry).flatMap(v => boundPropertyName(v) ?? []) : [];
    });
}

/**
 * A group is already bound when any of its slots carries a `$ref` to a theme
 * property in a field adoption would write. Checked per slot rather than per
 * group because a partially-bound group still counts as adopted — rebinding
 * the remainder is the repair path, not adoption's job.
 *
 * A `$ref` to anything else is the object's own: a state's colour, material,
 * roughness or metallic is bound to `$color_0`, `$roughness_0` and the like
 * when it is first set (`setMaterialColorForState` and kin), and its value
 * lives in that state. That is not a style's binding, so it is neither
 * adopted over nor, for Studio's gate, released: see {@link hasOwnBinding}.
 */
function isBound(materials: MaterialData[] | undefined, group: MaterialGroup): boolean {
    return boundNames(materials, group).some(isThemeProperty);
}

/** Whether a slot of the group is bound to a property that is not the theme's, whose value adoption would orphan. */
function hasOwnBinding(materials: MaterialData[] | undefined, group: MaterialGroup): boolean {
    return boundNames(materials, group).some(name => !isThemeProperty(name));
}

/**
 * The roles a group's existing bindings already occupy — read from the binding
 * names themselves (`$structureSrc` → `structure`), so the answer reflects what
 * is actually bound rather than what some earlier plan intended.
 */
function boundRolesOf(materials: MaterialData[] | undefined, group: MaterialGroup): Set<MaterialRole> {
    const roles = new Set<MaterialRole>();
    for (const i of group.slotIndices) {
        const entry = authoredFor(materials, i);
        if (!entry) continue;
        for (const field of adoptionFieldValues(entry)) {
            if (typeof field !== 'string' || !isPropertyBinding(field)) continue;
            const name = boundPropertyName(field);
            const role = name ? roleForPropertyName(name) : null;
            if (role) roles.add(role);
        }
    }
    return roles;
}

/**
 * A group carries a user literal when it has an authored, non-binding material
 * value — a src/color string, or a hand-tuned numeric factor (set via
 * `applyMaterialFactor` on an unbound slot). An *absent* entry is not a user
 * edit — it is an untouched slot, and treating those as user-owned is what
 * silently excluded every freshly imported object from the scene's theme in
 * the previous implementation.
 *
 * ⚠️ Presence of a literal is not sufficient on its own — see `everAdopted` in
 * {@link planAdoption}. This answers "is there an authored value here", and the
 * caller decides whether that value can mean "the user chose this over the
 * theme", which requires there to be a theme.
 */
function hasUserLiteral(materials: MaterialData[] | undefined, group: MaterialGroup): boolean {
    return group.slotIndices.some(i => {
        const entry = authoredFor(materials, i);
        if (!entry) return false;
        return adoptionFieldValues(entry).some(v =>
            (typeof v === 'string' && v !== '' && !isPropertyBinding(v))
            || typeof v === 'number');
    });
}

/**
 * Plan an object's adoption.
 *
 * Role resolution is **per group**: a group whose material name declares a role
 * gets that role, whatever its siblings do. The name is the author saying what
 * the material *is*, and that statement doesn't stop being true because the
 * material next to it was left generic — under the older all-or-nothing rule a
 * single unnamed sibling threw away every name on the asset, which is the one
 * outcome nobody authoring those names asked for.
 *
 * An unnamed group sitting next to a named one is skipped with
 * `'no-declared-role'` rather than handed the next ordinal. Ordinals would
 * scramble the named siblings, and an ordinal role is a decoration painted onto
 * a material whose author said nothing about it — a guess made where nothing
 * forces one. Nothing consumes the skip today, so in practice the group keeps
 * its authored material: for a kit asset's `fill_BG` that is the wanted
 * outcome, and for ordinary mixed content it is the honest one. The skip
 * carries the group's `slotIndices`, so a semantic fill/background pass can
 * address exactly those slots when one exists (#727).
 *
 * Positional assignment therefore runs only when NO group declares a role at
 * all — no naming convention to read, so position is the only signal left. That
 * fallback orders groups by material name rather than by mesh order. Mesh order
 * changes when an asset is re-exported with its meshes reordered, which would
 * silently re-point every slot; the name is stable.
 */
export function planAdoption(
    slots: MaterialSlot[],
    materials?: MaterialData[],
    options: AdoptionOptions = {},
): AdoptionPlan {
    const preserveUserLiterals = options.preserveUserLiterals ?? true;
    const adoptUnlit = options.adoptUnlit ?? false;
    const groups = groupSlotsByMaterial(slots);

    const assignments: AdoptionAssignment[] = [];
    const surfaceAssignments: SurfaceColorAssignment[] = [];
    const skipped: AdoptionSkip[] = [];
    const releases: AdoptionRelease[] = [];
    const warnings: string[] = [];

    // Roles already occupied on this object — bindings from an earlier
    // adoption. Without this, the ordinal fallback restarts at zero and a
    // later pass (a skip cleared, a material added) doubles up on `structure`.
    // Collected across ALL groups before any decision, so the answer doesn't
    // depend on which group is examined first.
    const takenRoles = new Set<MaterialRole>();
    for (const group of groups) {
        for (const role of boundRolesOf(materials, group)) takenRoles.add(role);
    }

    // Whether this object has ever joined a theme. Nothing bound anywhere means
    // it hasn't — and that decides what "hand-picked" can even mean: a literal
    // is a user edit only *relative to a theme the object already follows*. On
    // an unadopted object there is no theme to have deviated from, so its
    // literals are simply the scene's existing state, and asking for a style is
    // the request to replace them.
    //
    // Skipping them instead makes the first apply on any already-textured scene
    // a silent no-op — the same mistake as reading an absent entry as a user
    // edit, one step along: that excluded fresh imports, this excludes
    // everything else.
    const everAdopted = takenRoles.size > 0;

    const adoptable: MaterialGroup[] = [];
    for (const group of groups) {
        // Content first, and BEFORE the bound check: a bound content group is
        // pre-guard damage, and reading it as 'already-bound' is exactly how
        // the damage stays permanent — every apply keeps resolving role
        // materials over the baked text.
        if (!adoptUnlit && group.unlit) {
            if (isBound(materials, group)) {
                releases.push({ materialName: group.materialName, slotIndices: group.slotIndices });
            } else {
                skipped.push({ materialName: group.materialName, slotIndices: group.slotIndices, reason: 'unlit-content' });
            }
            continue;
        }
        if (isBound(materials, group)) {
            skipped.push({ materialName: group.materialName, slotIndices: group.slotIndices, reason: 'already-bound' });
            continue;
        }
        // The object's own binding, whatever the theme: the value it holds is the user's,
        // and a style would bind the field over it and leave that value shown in no state.
        if (hasOwnBinding(materials, group)) {
            skipped.push({ materialName: group.materialName, slotIndices: group.slotIndices, reason: 'user-literal' });
            continue;
        }
        if (preserveUserLiterals && everAdopted && hasUserLiteral(materials, group)) {
            skipped.push({ materialName: group.materialName, slotIndices: group.slotIndices, reason: 'user-literal' });
            continue;
        }
        adoptable.push(group);
    }

    // Claiming existing materials is correct but not invisible — the object did
    // look like something before. Surface it rather than let the change be a
    // surprise.
    if (!everAdopted && preserveUserLiterals) {
        const claimed = adoptable.filter(g => hasUserLiteral(materials, g)).length;
        if (claimed > 0) {
            warnings.push(
                `${claimed} material${claimed === 1 ? '' : 's'} already had an authored value; ` +
                `the first adoption of this object replaces ${claimed === 1 ? 'it' : 'them'}`,
            );
        }
    }

    // The surface axis is decided first and independently: a name that
    // declares a surface colour has declared something, so it is neither
    // handed a material role by ordinal nor skipped as unnamed. That is the
    // whole of #758 — before it, `fill_BG` resolved nothing and fell to
    // `no-declared-role` (or, before #757, dragged the whole object to the
    // ordinal tier).
    const surfaceNamed = adoptable.filter(g => g.declaredSurfaceRole !== null);
    // Co-assignment is surfaced on this axis for the same reason it is on the
    // material one: two names resolving to one role means two groups driven by
    // a single style value, which the editor should be able to say out loud.
    //
    // Reachable today, contrary to an earlier note here that called it
    // forward-proofing: groups are keyed by the material's EXACT name, while
    // roles resolve through `normalizeRoleKey`. So a GLB carrying both
    // `fill_BG` and `Fill-BG` is two groups folding to one role — different
    // slots, one style value, silently, unless this says so.
    const seenSurface = new Map<SurfaceRole, string>();
    for (const group of surfaceNamed) {
        const role = group.declaredSurfaceRole!;
        const prior = seenSurface.get(role);
        if (prior !== undefined) {
            warnings.push(`"${group.materialName}" and "${prior}" both declare the surface "${role}" — co-assigned`);
        }
        seenSurface.set(role, group.materialName);
        surfaceAssignments.push({
            materialName: group.materialName,
            role,
            slotIndices: group.slotIndices,
        });
    }
    const forMaterialAxis = adoptable.filter(g => g.declaredSurfaceRole === null);

    const named = forMaterialAxis.filter(g => g.declaredRole !== null);
    const unnamed = forMaterialAxis.filter(g => g.declaredRole === null);

    const tier: AdoptionTier = named.length > 0 ? 'role-name' : 'ordinal';

    if (tier === 'role-name') {
        // Two names declaring the same role (`Structure` + `Material_01`) are
        // co-assigned — they asked for the same thing — but surfaced, so the
        // editor can show it rather than let two groups silently share one
        // material.
        const seen = new Map<MaterialRole, string>();
        for (const group of named) {
            // named: declaredRole !== null by the filter above — a plain
            // predicate, not a `g is X` type guard, so TS doesn't narrow it.
            const role = group.declaredRole!;
            const prior = seen.get(role) ?? (takenRoles.has(role) ? '(already bound)' : undefined);
            if (prior !== undefined) {
                warnings.push(`"${group.materialName}" and "${prior}" both declare the role "${role}" — co-assigned`);
            }
            seen.set(role, group.materialName);
            assignments.push({ materialName: group.materialName, role, slotIndices: group.slotIndices });
        }
        for (const group of unnamed) {
            skipped.push({ materialName: group.materialName, slotIndices: group.slotIndices, reason: 'no-declared-role' });
        }
    } else {
        // Positional fallback, ordered by material name rather than mesh order:
        // mesh order changes when an asset is re-exported with meshes
        // reordered, which would silently re-point every slot; the name is
        // stable. Assignment draws from the roles NOT already bound on the
        // object, so re-adoption fills gaps instead of doubling up; when every
        // role is taken the full cycle is the only option left.
        const freeRoles = MATERIAL_ROLES.filter(r => !takenRoles.has(r));
        const pool = freeRoles.length > 0 ? freeRoles : MATERIAL_ROLES;
        const ordered = [...forMaterialAxis].sort((a, b) => a.materialName.localeCompare(b.materialName));
        // Gated on there being groups to warn ABOUT, not just on the roles
        // being taken. Since the surface axis is split off first, an object
        // whose only adoptable group is surface-named reaches this branch with
        // an empty `ordered` — and warned about a collision that was not
        // happening, on an otherwise clean "just the surface colour" adoption.
        if (freeRoles.length === 0 && ordered.length > 0) {
            warnings.push(
                'every role is already bound on this object — these groups share roles with existing bindings',
            );
        }
        // Co-assignment is surfaced here for the same reason the role-name tier
        // surfaces it: the cycle means more material groups than roles, so two
        // groups end up driven by one style value, which the editor should be
        // able to say out loud rather than leave the user to discover.
        const seen = new Map<MaterialRole, string>();
        ordered.forEach((group, ordinal) => {
            const role = pool[ordinal % pool.length];
            const prior = seen.get(role);
            if (prior !== undefined) {
                warnings.push(`"${group.materialName}" and "${prior}" both landed on the role "${role}" — more material groups than roles`);
            }
            seen.set(role, group.materialName);
            assignments.push({
                materialName: group.materialName,
                role,
                slotIndices: group.slotIndices,
            });
        });
    }

    return {
        tier,
        assignments,
        surfaceAssignments,
        skipped,
        releases,
        // Mesh SLOTS this plan touches, across both axes — not the number of
        // property writes, which is four per slot on the material axis (one
        // per suffix) and one on the surface axis. Counting slots is what this
        // field meant before the second axis existed; surface slots join the
        // same count rather than being measured differently.
        bindCount: assignments.reduce((n, a) => n + a.slotIndices.length, 0)
            + surfaceAssignments.reduce((n, a) => n + a.slotIndices.length, 0),
        warnings,
    };
}
